import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Col, Row } from "reactstrap";

// Store
import { useTypedSelector } from "../../store/models/root.interface";
import {
  setEventsPeriod,
  fetchEvents,
  fetchStatistics,
  clearFilters,
  EventsFilters,
} from "../../store/actions/events.actions";

import { EventsPeriod, Event } from "../../api";

import DataTable from "../../common/components/ReactTable";
import DateRange from "../../common/components/DateRange";
import DownloadCsv from "../../common/components/DownloadCsv";
import Columns from "../../common/components/ReactTable/columns/EventsListColumns";
import CsvColumns from "../../common/components/ReactTable/columns/EventsCsvColumns";
import TitleMeta from "../../common/components/TitleMeta";

import Layout from "../../components/Layout/Layout";

import EventsListSidebarRoot from "./Sidebar/EventsListSidebarRoot";

interface Params {
  eventId: string | undefined;
  ticketId: string | undefined;
}

const EventsList = () => {

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Store
  const period = useTypedSelector(state => state.event.period);
  const eventUidsByPeriod = useTypedSelector(state => state.event.eventUidsByPeriod);
  const filteredEventUids = useTypedSelector(state => state.event.filteredEventUids);
  const eventsByUId = useTypedSelector(state => state.event.eventsByUid);

  // State
  const [items, setItems] = useState<Event[]>([]);
  const [filters, setFilters] = useState<EventsFilters>();

  // Memos
  const isLoading = useMemo(
    () => eventUidsByPeriod[period] === "loading" && !Object.values(eventUidsByPeriod).some(x => typeof x !== "string"),
    [eventUidsByPeriod, period]
  );
  const isFiltering = useMemo(
    () => filteredEventUids === "loading" || (eventUidsByPeriod[period] === "loading" && Object.values(eventUidsByPeriod).some(x => typeof x !== "string")),
    [eventUidsByPeriod, period, filteredEventUids]
  );
  const isError = useMemo(
    () => (filters ? filteredEventUids : eventUidsByPeriod[period]) === "error",
    [period, eventUidsByPeriod, filters, filteredEventUids]
  );

  const eventInfo = useMemo(() => {
    const eventId = new URLSearchParams(location.search).get("eventId");
    const ticketId = new URLSearchParams(location.search).get("ticketId");
    if (eventId && ticketId) {
      return {
        eventId,
        ticketId,
      }
    }
  }, [location.search]);

  useEffect(() => {
    const eventUids = filters ? filteredEventUids ?? eventUidsByPeriod[period] : eventUidsByPeriod[period];
    if (eventUids && eventUids !== "loading") {
      setItems(eventUids === "error" ? [] : eventUids.map(id => eventsByUId[id] as Event));
    }
  }, [period, eventUidsByPeriod, filters, filteredEventUids]);

  useEffect(() => {
    dispatch(fetchEvents(period, filters));
    dispatch(fetchStatistics(period, filters));
  }, [period, filters]);

  const handleSelect = (event: Event) => {
    if (!eventInfo || eventInfo.eventId !== event.eventId || eventInfo.ticketId !== event.ticketId) {
      history.replace(`/events/list?eventId=${event.eventId}&ticketId=${event.ticketId}`);
    }
    else {
      history.replace("/events/list");
    }
  };

  const handleSelectDateRange = (period: EventsPeriod) => {
    dispatch(setEventsPeriod(period));
    if (eventInfo) {
      history.replace("/events/list");
    }
  };

  const handleFilters = (data: { id: string; value: any }[]) => {
    const filters: EventsFilters = {};
    if (data.length) {
      for (let i = 0; i < data.length; i++) {
        let key = data[i].id;
        let value = data[i].value;
        if (value) {
          switch (key) {
            case "name":
              filters.name = value;
              break;
            case "round":
              filters.round = value;
              break;
            case "type":
              filters.type = value;
              break;
          }
        }
      }
    }
    const anyFilters = Object.keys(filters).length !== 0;
    setFilters(anyFilters ? filters : void 0);
    if (!anyFilters) {
      dispatch(clearFilters());
    }
  };

  return (
    <>
      <TitleMeta title="Events list" />

      <Layout.SidebarPage>

        <Layout.Main>

          <Layout.Header>
            <Layout.Breadcrumb items={[["Events", "/events"]]} />
            <div className="d-flex align-items-start justify-content-between">
              {/* Heading */}
              <div className="d-flex align-items-center">
                <Layout.Title>Events List</Layout.Title>
                <div>
                  <DateRange
                    onChange={(e: any) => handleSelectDateRange(e)}
                    value={period}
                  />
                </div>
              </div>
              {/* Controls */}
              <div>
                <DownloadCsv
                  filename="Events List"
                  columns={CsvColumns}
                  data={items}
                />
              </div>
            </div>
          </Layout.Header>

          <Layout.Body>
            {isError ? (
              <p>Sorry, something went wrong.</p>
            ) : (
              <DataTable
                manualFilters
                columns={Columns}
                data={items}
                selected={event => !!eventInfo && event.eventId === eventInfo.eventId && event.ticketId === eventInfo.ticketId}
                onSelect={handleSelect}
                onChangeFilter={handleFilters}
                isLoading={isLoading}
                isFiltering={isFiltering}
                messages={{
                  noRecords: "No events found",
                  noMatches: "No matching events found"
                }}
              />
            )}
          </Layout.Body>

        </Layout.Main>

        <Layout.Sidebar>
          <EventsListSidebarRoot />
        </Layout.Sidebar>

      </Layout.SidebarPage>
    </>
  );
};

export default EventsList;
